import React, { useEffect } from 'react';
import { useTitle } from '../hooks/useTitle';
import { LanguageSelector } from '../components/LanguageSelector';
import { AdminBar } from '../components/AdminBar';
import { ArrowRight, Brain, Target, Users, Rocket, MessageCircle, CheckCircle2, Calendar, Clock, Lightbulb, BookOpen, Zap } from 'lucide-react';

export function AiBootcamp() {
  const { setTitle } = useTitle();
  
  useEffect(() => {
    setTitle('Bootcamp IA - Novster AI');
  }, [setTitle]);

  return (
    <div className="min-h-screen bg-white">
      <LanguageSelector />
      <AdminBar />

      {/* Hero Section */}
      <div className="bg-gradient-to-b from-orange-50 to-white">
        <div className="container mx-auto px-4 py-20">
          <h1 className="text-4xl md:text-6xl font-bold text-center text-gray-900 mb-6 animate-slideUp">
            Bootcamp IA : Utilisez l'IA comme un Pro pour{' '}
            <span className="text-orange-500">Booster Votre Business</span>
          </h1>
          <p className="text-xl text-center text-gray-600 max-w-3xl mx-auto mb-12 animate-slideUp">
            Passez de l'utilisation basique à la maîtrise stratégique de l'IA pour gagner du temps,
            améliorer la qualité de vos actions et générer du cash.
          </p>
          <div className="flex justify-center mb-12">
            <a
              href="https://tidycal.com/quentin.tousart/novster-ai"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 transform hover:scale-105 transition-all animate-slideUp animate-float shadow-lg"
            >
              Réserver ma place
              <span className="px-2 py-1 bg-orange-400 rounded-full text-sm">Places limitées</span>
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>

      {/* Format Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Format du Bootcamp</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all">
              <Calendar className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Durée</h3>
              <p className="text-gray-600">1 à 2 jours intensifs</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all">
              <Users className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Format</h3>
              <p className="text-gray-600">Présentiel ou en ligne</p>
            </div>
            <div className="text-center p-6 bg-white rounded-xl shadow-lg hover:shadow-xl transition-all">
              <Target className="w-12 h-12 text-orange-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Approche</h3>
              <p className="text-gray-600">Formation-action interactive</p>
            </div>
          </div>
        </div>
      </section>

      {/* Programme Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Programme Complet</h2>
          <div className="max-w-4xl mx-auto space-y-8">
            {[
              {
                title: "Acculturation & Diagnostic",
                icon: Brain,
                items: [
                  "Évaluation de votre niveau actuel",
                  "Identification des opportunités d'optimisation",
                  "Analyse de vos processus existants"
                ]
              },
              {
                title: "Maîtrise des Outils",
                icon: Zap,
                items: [
                  "Formation aux outils incontournables",
                  "Techniques de prompt engineering avancées",
                  "Création d'agents IA personnalisés"
                ]
              },
              {
                title: "Ateliers Pratiques",
                icon: Rocket,
                items: [
                  "Construction de votre business plan IA",
                  "Création de prompts personnalisés",
                  "Mise en place de stratégies marketing"
                ]
              },
              {
                title: "Transformation Stratégique",
                icon: Lightbulb,
                items: [
                  "Intégration de l'IA dans votre quotidien",
                  "Optimisation des processus internes",
                  "Création de nouveaux leviers de croissance"
                ]
              }
            ].map((module, index) => (
              <div key={module.title}
                   className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-all"
                   style={{ '--delay': `${0.2 * index}s` } as React.CSSProperties}>
                <div className="flex items-start gap-6">
                  <div className="bg-orange-100 rounded-full p-3">
                    {React.createElement(module.icon, { className: "w-8 h-8 text-orange-500" })}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold mb-4">{module.title}</h3>
                    <ul className="space-y-3">
                      {module.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0 mt-1" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Livrables Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Ce que vous obtenez</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {[
              {
                title: "Business Plan IA",
                description: "Un plan d'action concret optimisé par l'IA"
              },
              {
                title: "Stratégie Marketing",
                description: "Une stratégie de communication automatisée"
              },
              {
                title: "Boîte à Outils",
                description: "Des prompts et outils personnalisés"
              },
              {
                title: "Plan d'Action",
                description: "Une feuille de route d'intégration de l'IA"
              }
            ].map((livrable) => (
              <div key={livrable.title} className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all">
                <h3 className="text-xl font-bold mb-2">{livrable.title}</h3>
                <p className="text-gray-600">{livrable.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="py-20 bg-orange-500 text-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-16">Bonus Exclusifs</h2>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <MessageCircle className="w-12 h-12 mb-4" />
                <h3 className="text-xl font-bold mb-2">Support Continu</h3>
                <p>Accès à un groupe WhatsApp privé pour échanger et affiner vos stratégies</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <BookOpen className="w-12 h-12 mb-4" />
                <h3 className="text-xl font-bold mb-2">Ressources Premium</h3>
                <p>Accès à des prompts avancés et des ressources exclusives</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-8">Prêt à devenir un entrepreneur AI-first ?</h2>
          <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
            Inscrivez-vous maintenant et transformez votre business grâce à l'IA en 2025
          </p>
          <a
            href="https://tidycal.com/quentin.tousart/novster-ai"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-semibold flex items-center gap-2 mx-auto"
          >
            Je réserve ma place
            <ArrowRight className="w-5 h-5" />
          </a>
          <div className="flex justify-center gap-8 mt-8 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Places limitées</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Groupes restreints</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}